import { FC, ReactNode } from "react";
import { Link, useSegments, type Href } from "expo-router";

import { VnBox } from "@/components/VnBox";
import { VnSeparator } from "@/components/VnSeparator";
import { VnText } from "@/components/VnText/VnText";
import { ChildrenOrI18nType } from "@/i18n";
import { trackElementClicked } from "@/services/mixpanel";
import { cn } from "@/utils/tailwind";

const SIDEBAR_WIDTH = 256;

type VnSidebarPropsType = {
  children: ReactNode;
};

// Desktop sidebar - fixed on the left side (only used on desktop web)
export const VnSidebar: FC<VnSidebarPropsType> = ({ children }) => {
  return (
    <VnBox
      className="bg-sidebar border-border fixed inset-y-0 left-0 z-40 flex-col border-r"
      style={{ width: SIDEBAR_WIDTH }}>
      {children}
    </VnBox>
  );
};

type VnSidebarHeaderPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarHeader: FC<VnSidebarHeaderPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("flex-col gap-2 p-4", className)}>{children}</VnBox>
  );
};

type VnSidebarFooterPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarFooter: FC<VnSidebarFooterPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("mt-auto flex-col gap-2 p-4", className)}>
      {children}
    </VnBox>
  );
};

type VnSidebarContentPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarContent: FC<VnSidebarContentPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("min-h-0 flex-1 flex-col gap-2 p-2", className)}>
      {children}
    </VnBox>
  );
};

type VnSidebarGroupPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarGroup: FC<VnSidebarGroupPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("relative w-full min-w-0 flex-col p-2", className)}>
      {children}
    </VnBox>
  );
};

type VnSidebarGroupLabelPropsType = {
  className?: string;
} & ChildrenOrI18nType;

export const VnSidebarGroupLabel: FC<VnSidebarGroupLabelPropsType> = ({
  children,
  i18nKey,
  i18nOptions,
  className,
}) => {
  return (
    <VnBox
      className={cn(
        "h-8 shrink-0 flex-row items-center rounded-md px-2",
        className,
      )}>
      {children ?? (
        <VnText
          variant="smMedium"
          i18nKey={i18nKey}
          i18nOptions={i18nOptions}
          className="text-sidebar-foreground/70"
        />
      )}
    </VnBox>
  );
};

type VnSidebarGroupContentPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarGroupContent: FC<VnSidebarGroupContentPropsType> = ({
  children,
  className,
}) => {
  return <VnBox className={cn("w-full", className)}>{children}</VnBox>;
};

type VnSidebarMenuPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarMenu: FC<VnSidebarMenuPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("w-full min-w-0 flex-col gap-1", className)}>
      {children}
    </VnBox>
  );
};

type VnSidebarMenuItemPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarMenuItem: FC<VnSidebarMenuItemPropsType> = ({
  children,
  className,
}) => {
  return <VnBox className={cn("relative", className)}>{children}</VnBox>;
};

type VnSidebarMenuButtonPropsType = {
  children: ReactNode;
  href: Href;
  isActive?: boolean;
  className?: string;
  analyticsLabel?: string;
};

export const VnSidebarMenuButton: FC<VnSidebarMenuButtonPropsType> = ({
  children,
  href,
  isActive = false,
  className,
  analyticsLabel,
}) => {
  const segments = useSegments();

  const handlePress = () => {
    if (analyticsLabel) {
      trackElementClicked({
        route: `/${segments.join("/")}`,
        elementTextKey: analyticsLabel,
        elementType: "menu_item",
      });
    }
  };

  return (
    <Link href={href} onPress={handlePress} asChild>
      <VnBox
        className={cn(
          "w-full flex-row items-center gap-2 overflow-hidden rounded-sm p-2",
          {
            "bg-sidebar-accent": isActive,
          },
          className,
        )}>
        {children}
      </VnBox>
    </Link>
  );
};

type VnSidebarSeparatorPropsType = {
  className?: string;
};

export const VnSidebarSeparator: FC<VnSidebarSeparatorPropsType> = ({
  className,
}) => {
  return (
    <VnSeparator className={cn("bg-sidebar-border mx-2 w-auto", className)} />
  );
};

type VnSidebarInsetPropsType = {
  children: ReactNode;
  className?: string;
};

export const VnSidebarInset: FC<VnSidebarInsetPropsType> = ({
  children,
  className,
}) => {
  return (
    <VnBox className={cn("bg-background relative flex-1", className)}>
      {children}
    </VnBox>
  );
};
